import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const ITARvsEAR = () => {
  const comparisonData = [
    {
      category: "Governing Authority",
      itar: "U.S. Department of State - Directorate of Defense Trade Controls (DDTC)",
      ear: "U.S. Department of Commerce - Bureau of Industry and Security (BIS)"
    },
    {
      category: "Controlled Items",
      itar: "Defense articles, defense services, and related technical data on the U.S. Munitions List (USML)",
      ear: "Dual-use items and commercial products on the Commerce Control List (CCL)"
    },
    {
      category: "Registration Required",
      itar: "Yes - Annual registration required for manufacturers, exporters, and brokers ($3,000+ fee)",
      ear: "No - No registration requirement (except for certain encryption items)"
    },
    {
      category: "Item Classification",
      itar: "United States Munitions List (USML) - 21 categories",
      ear: "Commerce Control List (CCL) - Export Control Classification Numbers (ECCNs)"
    },
    {
      category: "Penalty for Violations",
      itar: "Criminal: Up to $1M per violation and 20 years imprisonment. Civil: Up to $500K per violation",
      ear: "Criminal: Up to $1M and 20 years imprisonment. Administrative: Up to $364,992 per violation"
    },
    {
      category: "Re-export Authorization",
      itar: "Typically requires State Department approval for all re-exports",
      ear: "May not require authorization depending on destination and ECCN"
    },
    {
      category: "Technology Transfer",
      itar: "Strictly controlled - requires Technical Assistance Agreement (TAA) or Manufacturing License Agreement (MLA)",
      ear: "Controlled but with more flexibility and license exceptions available"
    },
    {
      category: "Foreign Person Access",
      itar: "Deemed export - prior authorization required before releasing to foreign nationals",
      ear: "Deemed export - authorization may be required depending on nationality and technology"
    },
    {
      category: "License Processing Time",
      itar: "60-90+ days typical for complex items",
      ear: "4-8 weeks typical, with 9-day goal for standard cases"
    },
    {
      category: "Recordkeeping",
      itar: "5 years from export or other transaction",
      ear: "5 years from export, re-export, or transfer"
    }
  ];

  return (
    <div className="comparison-page">
      <SEO 
        title="ITAR vs EAR: Complete Comparison Guide for Export Controls"
        description="Comprehensive comparison of ITAR and EAR export control regulations. Understand the key differences between State Department ITAR and Commerce Department EAR requirements."
        keywords="ITAR vs EAR, export controls comparison, ITAR regulations, EAR regulations, USML vs CCL, defense articles, dual-use items"
        path="/itar-vs-ear"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'ITAR vs EAR', path: '/itar-vs-ear' }
      ]} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              ITAR vs EAR: Complete Comparison
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Understanding the differences between ITAR (International Traffic in Arms Regulations) and EAR (Export Administration Regulations) is fundamental to export compliance. This comprehensive guide compares both regulatory frameworks to help you determine which applies to your products.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-red-900 mb-4">ITAR</h3>
              <p className="text-red-800 mb-4 font-semibold">Specifically designed for military items</p>
              <ul className="space-y-2 text-red-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Defense articles and services</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>State Department jurisdiction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Registration required</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>More restrictive controls</span>
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-blue-900 mb-4">EAR</h3>
              <p className="text-blue-800 mb-4 font-semibold">Controls dual-use and commercial items</p>
              <ul className="space-y-2 text-blue-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Dual-use items and technology</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Commerce Department jurisdiction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>No registration requirement</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>More license exceptions available</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Side-by-Side Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="text-left p-4 font-bold text-lg">Category</th>
                  <th className="text-left p-4 font-bold text-lg">ITAR</th>
                  <th className="text-left p-4 font-bold text-lg">EAR</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-900 border-b border-gray-200">
                      {row.category}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.itar}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.ear}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Key Decision Factors */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Which Regulation Applies to Your Product?
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <AlertCircle className="h-7 w-7 text-red-600" />
                Your Product is Likely ITAR-Controlled If:
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  <span>It's specifically designed, developed, configured, adapted, or modified for a military application</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  <span>It's listed on the U.S. Munitions List (USML)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  <span>It has no predominant civil applications</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  <span>It does not have performance equivalent to commercial items</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <AlertCircle className="h-7 w-7 text-blue-600" />
                Your Product is Likely EAR-Controlled If:
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>It has both commercial and military applications (dual-use)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>It's not specifically designed for military use</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>It's not on the USML but may be on the Commerce Control List</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>It's a commercial product with export control considerations</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-8">
              <h3 className="text-xl font-bold text-amber-900 mb-3 flex items-center gap-2">
                <AlertCircle className="h-6 w-6" />
                Not Sure? Request a Commodity Jurisdiction (CJ) Determination
              </h3>
              <p className="text-amber-800">
                When jurisdiction is unclear, you can request a formal Commodity Jurisdiction determination from the U.S. State Department. This process provides an official determination and legal certainty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Misconceptions */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Common Misconceptions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
              <div className="flex items-start gap-3 mb-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-gray-900">Myth</h3>
              </div>
              <p className="text-gray-700 italic">"Commercial items don't need export licenses"</p>
            </div>
            <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-gray-900">Reality</h3>
              </div>
              <p className="text-gray-700">Many commercial items are controlled under EAR and require licenses for certain destinations or end-uses.</p>
            </div>

            <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
              <div className="flex items-start gap-3 mb-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-gray-900">Myth</h3>
              </div>
              <p className="text-gray-700 italic">"EAR is always less restrictive than ITAR"</p>
            </div>
            <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-gray-900">Reality</h3>
              </div>
              <p className="text-gray-700">Certain EAR-controlled items (especially those related to WMD) can have very strict controls comparable to ITAR.</p>
            </div>

            <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
              <div className="flex items-start gap-3 mb-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-gray-900">Myth</h3>
              </div>
              <p className="text-gray-700 italic">"I can self-determine jurisdiction without consequences"</p>
            </div>
            <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-gray-900">Reality</h3>
              </div>
              <p className="text-gray-700">Incorrect jurisdiction determinations can result in serious violations. When unclear, obtain a formal CJ determination.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Help Determining ITAR or EAR Jurisdiction?
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Our export control specialists can help you navigate commodity jurisdiction determinations and ensure proper classification of your products.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
                Schedule Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/services/product-classification">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 h-14 font-semibold">
                Learn About Classification Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Related Resources</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/glossary" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="font-bold text-gray-900 mb-2">Trade Glossary</h4>
              <p className="text-sm text-gray-600">Definitions of export control terms</p>
            </Link>
            <Link to="/services/export-controls" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="font-bold text-gray-900 mb-2">Export Control Services</h4>
              <p className="text-sm text-gray-600">Comprehensive compliance solutions</p>
            </Link>
            <Link to="/faq" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="font-bold text-gray-900 mb-2">FAQs</h4>
              <p className="text-sm text-gray-600">Common export control questions</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ITARvsEAR;
